import {
	ApiRequest,
	convertStartDateToUTC,
	convertEndDateToUTC,
	formatDate,
} from '@zeal/zeal-lib';
import { ENDPOINTS } from '../endpoints';
import {
	TBurnedReports,
	TVouchersExpiredReports,
	TEarnedReports,
	TPointsExpiredReports,
	TRedeemedReports,
	TMemberInsights,
	TPointsInsights,
	LoyaltyChannel,
	TUpdateLoyaltyChannel,
} from '@modules/LoyaltyProgramme/types';

export async function freemiumLoyaltyMemberInsights({
	queryKey: [, filters],
}: any) {
	const request = new ApiRequest(ENDPOINTS.loyaltyProgramme.memberInsights);

	return appendAnalyticsFiltersQuery(request, filters).GET<
		ApiResponse<TMemberInsights>
	>();
}

export async function freemiumLoyaltyPointsInsights({
	queryKey: [, filters],
}: any) {
	const request = new ApiRequest(ENDPOINTS.loyaltyProgramme.pointsInsights);

	return appendAnalyticsFiltersQuery(request, filters).GET<
		ApiResponse<TPointsInsights>
	>();
}

export async function freemiumEarned({ queryKey: [, filters] }: any) {
	const request = new ApiRequest(ENDPOINTS.loyaltyProgramme.earned.report);

	return appendFiltersQuery(request, filters)
		.addQuery('page', filters?.page || 1)
		.GET<ApiResponseWithPagination<TEarnedReports>>();
}

export async function freemiumBurned({ queryKey: [, filters] }: any) {
	const request = new ApiRequest(ENDPOINTS.loyaltyProgramme.burned.report);

	return appendFiltersQuery(request, filters)
		.addQuery('page', filters?.page || 1)
		.GET<ApiResponseWithPagination<TBurnedReports>>();
}

export async function freemiumRedeemed({ queryKey: [, filters] }: any) {
	const request = new ApiRequest(ENDPOINTS.loyaltyProgramme.redeemed.report);

	return appendFiltersQuery(request, filters)
		.addQuery('page', filters?.page || 1)
		.GET<ApiResponseWithPagination<TRedeemedReports>>();
}

export async function freemiumPointsExpired({ queryKey: [, filters] }: any) {
	const request = new ApiRequest(
		ENDPOINTS.loyaltyProgramme.pointsExpired.report
	);

	return appendFiltersQuery(request, filters)
		.addQuery('page', filters?.page || 1)
		.GET<ApiResponseWithPagination<TPointsExpiredReports>>();
}

export async function freemiumVouchersExpired({ queryKey: [, filters] }: any) {
	const request = new ApiRequest(
		ENDPOINTS.loyaltyProgramme.vouchersExpired.report
	);

	return appendFiltersQuery(request, filters)
		.addQuery('page', filters?.page || 1)
		.GET<ApiResponseWithPagination<TVouchersExpiredReports>>();
}

export async function freemiumEarnedExport(filters: any) {
	const request = new ApiRequest(ENDPOINTS.loyaltyProgramme.earned.export);

	return appendFiltersQuery(request, filters).POST<ApiResponse<any>>('');
}

export async function freemiumBurnedExport(filters: any) {
	const request = new ApiRequest(ENDPOINTS.loyaltyProgramme.burned.export);

	return appendFiltersQuery(request, filters).POST<ApiResponse<any>>('');
}

export async function freemiumRedeemedExport(filters: any) {
	const request = new ApiRequest(ENDPOINTS.loyaltyProgramme.redeemed.export);

	return appendFiltersQuery(request, filters).POST<ApiResponse<any>>('');
}

export async function updateLoyaltyChannel(loyaltyChannel: LoyaltyChannel) {
	return new ApiRequest(ENDPOINTS.loyaltyProgramme.updateLoyaltyChannel)
		.addHeader('Content-Type', 'application/json')
		.PATCH<TUpdateLoyaltyChannel>(
			JSON.stringify({ loyalty_channel: loyaltyChannel })
		);
}

export async function freemiumPointsExpiredExport(filters: any) {
	const request = new ApiRequest(
		ENDPOINTS.loyaltyProgramme.pointsExpired.export
	);

	return appendFiltersQuery(request, filters).POST<ApiResponse<any>>('');
}

export async function freemiumVouchersExpiredExport(filters: any) {
	const request = new ApiRequest(
		ENDPOINTS.loyaltyProgramme.vouchersExpired.export
	);

	return appendFiltersQuery(request, filters).POST<ApiResponse<any>>('');
}

export function formatToApiDate(date: any) {
	return formatDate(date, 'YYYY-MM-DD');
}

function appendFiltersQuery(request: ApiRequest, filters: filters) {
	if (filters) {
		Object.entries(filters).forEach(([key, value]) => {
			if (value) {
				if (key === 'from' || key === 'last_from') {
					request.addQuery(key, convertStartDateToUTC(value));
				} else if (key === 'to' || key === 'last_to') {
					request.addQuery(key, convertEndDateToUTC(value));
				}
			}
		});
	}

	return request;
}

function appendAnalyticsFiltersQuery(request: ApiRequest, filters: filters) {
	if (filters) {
		Object.entries(filters).forEach(([key, value]) => {
			if (value) {
				if (key === 'branch_uuid' && typeof value === 'string') {
					request.addQuery(key, value);
				} else if (key === 'from') {
					request.addQuery(`date_filter[from]`, convertStartDateToUTC(value));
				} else if (key === 'to') {
					request.addQuery(`date_filter[to]`, convertEndDateToUTC(value));
				} else if (key === 'last_from') {
					request.addQuery(`comparison[from]`, convertStartDateToUTC(value));
				} else if (key === 'last_to') {
					request.addQuery(`comparison[to]`, convertEndDateToUTC(value));
				}
			}
		});
		request.addQuery(`date_filter[type]`, 'range');
	}

	return request;
}

type filters = {
	from: string | Date;
	to: string | Date;
	last_from?: string | Date;
	last_to?: string | Date;
	branch_uuid?: string;
};
